import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { useTranslation } from '../../commons/useTranslasion';
import { useRecoilState } from 'recoil';
import { authAtom } from '../../states/usuarios/AuthState';
import { useAxiosWrapper } from '../../services/axiosWrapper';
import { useUsuarioActions } from '../../states/usuarios/usuario.actions';
import { toast } from 'react-toastify';

const DEFAULT_AVATAR = 'https://miro.medium.com/v2/resize:fit:1400/1*g09N-jl7JtVjVZGcd-vL2g.jpeg';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MIN_IMAGE_WIDTH = 200;
const MIN_IMAGE_HEIGHT = 200;
const IDEAL_IMAGE_WIDTH = 400;
const IDEAL_IMAGE_HEIGHT = 400;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ImagePreview = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '300px',
  margin: '0 auto',
  marginTop: theme.spacing(2),
  position: 'relative',
  '& img': {
    width: '100%',
    borderRadius: '4px',
  },
}));

const ProfilePhoto: React.FC<any> = () => {
  const { translate } = useTranslation();
  const [auth, setAuth] = useRecoilState(authAtom);
  const axios = useAxiosWrapper();
  const usuarioActions = useUsuarioActions();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [tempPhotoUrl, setTempPhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [viewFullPhoto, setViewFullPhoto] = useState<boolean>(false);
  const [photoUrl, setPhotoUrl] = useState<string>(auth?.foto && auth.foto !== "" ? auth.foto : DEFAULT_AVATAR);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (auth?.foto && auth.foto !== "") {
      setPhotoUrl(auth.foto);
    }
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleViewFullPhoto = () => {
    handleClose();
    setViewFullPhoto(true);
  };

  const handleCloseFullPhoto = () => {
    setViewFullPhoto(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setTempPhotoUrl(null);
  };

  const handleUpload = () => {
    handleClose();
    setOpenDialog(true);
  };

  const handleSavePhoto = async () => {
    if (!tempPhotoUrl) return;

    try {
      setLoading(true);

      const base64Response = await fetch(tempPhotoUrl);
      const blob = await base64Response.blob();

      const formData = new FormData();
      formData.append('foto', blob, 'profile-photo.jpg');

      const response = await usuarioActions.uploadPhoto(formData);

      if (response) {
        const photoUrlFromServer = response;

        setPhotoUrl(photoUrlFromServer);
        if (auth) {
          const updatedAuth = {
            ...auth,
            foto: photoUrlFromServer
          };
          setAuth(updatedAuth);
          localStorage.setItem('usuario', JSON.stringify(updatedAuth));
          toast.success(translate('fotoAtualizadaComSucesso'));
        }
      }
    } catch (error) {
      console.error(`${translate('erroSalvarFoto')}: `, error);
    } finally {
      setLoading(false);
      handleDialogClose();
    }
  };

  const handleRemove = async () => {
    try {
      setLoading(true);
      await usuarioActions.removePhoto();

      setPhotoUrl(DEFAULT_AVATAR);

      if (auth) {
        const updatedAuth = {
          ...auth,
          foto: ''
        };
        setAuth(updatedAuth);
        localStorage.setItem('usuario', JSON.stringify(updatedAuth));
        toast.success(translate('fotoRemovidaComSucesso'));
      }
    } catch (error) {
      console.error(`${translate('erroRemoverFoto')}: `, error);
      toast.error(translate('erroRemoverFoto'));
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const validateFile = (file: File): boolean => {
    // Validar formato do arquivo
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error(translate('formatoInvalido'));
      return false;
    }

    // Validar tamanho do arquivo
    if (file.size > MAX_FILE_SIZE) {
      toast.error(translate('tamanhoExcedido'));
      return false;
    }

    return true;
  };

  const validateAndResizeImage = (dataUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width < MIN_IMAGE_WIDTH || img.height < MIN_IMAGE_HEIGHT) {
          toast.error(translate('dimensaoInvalida'));
          reject(new Error('Dimensões inválidas'));
          return;
        }

        if (img.width <= IDEAL_IMAGE_WIDTH && img.height <= IDEAL_IMAGE_HEIGHT) {
          resolve(dataUrl);
          return;
        }

        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > IDEAL_IMAGE_WIDTH) {
            height = Math.round(height * IDEAL_IMAGE_WIDTH / width);
            width = IDEAL_IMAGE_WIDTH;
          }
        } else {
          if (height > IDEAL_IMAGE_HEIGHT) {
            width = Math.round(width * IDEAL_IMAGE_HEIGHT / height);
            height = IDEAL_IMAGE_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        const format = dataUrl.split(';')[0].split('/')[1];
        resolve(canvas.toDataURL(`image/${format}`, 0.9));
      };

      img.onerror = () => {
        reject(new Error('Erro ao carregar imagem'));
      };

      img.src = dataUrl;
    });
  };

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!validateFile(file)) {
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const result = e.target?.result as string;
      try {
        const resizedImage = await validateAndResizeImage(result);
        setTempPhotoUrl(resizedImage);
      } catch (error) {
        event.target.value = '';
      }
    };
    reader.readAsDataURL(file);
  }, [translate]);

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <IconButton size="small" sx={{ ml: 1 }} onClick={handleClick}>
        <Avatar
          alt="Foto do Perfil"
          src={photoUrl}
          sx={{ width: 32, height: 32 }}
        />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleUpload}>
          <CloudUploadIcon fontSize="small" sx={{ mr: 1 }} />
          {translate('alterarFoto')}
        </MenuItem>
        {photoUrl !== DEFAULT_AVATAR && (
          <>
            <MenuItem onClick={handleViewFullPhoto}>
              <PhotoCameraIcon fontSize="small" sx={{ mr: 1 }} />
              {translate('visualizarFoto')}
            </MenuItem>
            <MenuItem onClick={handleRemove} disabled={loading}>
              {loading ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : (
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              )}
              {translate('removerFoto')}
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('alterarFotoPerfil')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {translate('selecioneFoto')}
          </Typography>

          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
            {translate('formatosSuportados')}
          </Typography>

          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
            • Tamanho máximo: 5MB
          </Typography>

          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
            • Dimensão mínima: {MIN_IMAGE_WIDTH}x{MIN_IMAGE_HEIGHT} pixels
          </Typography>

          <Button
            variant="contained"
            startIcon={<PhotoCameraIcon />}
            sx={{ mt: 2 }}
            onClick={triggerFileInput}
            disabled={loading}
          >
            {translate('escolherFoto')}
          </Button>
          <VisuallyHiddenInput
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif"
            onChange={handleFileChange}
          />

          {tempPhotoUrl && (
            <ImagePreview>
              <img src={tempPhotoUrl} alt={translate('previaFoto')} />
            </ImagePreview>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={loading}>
            {translate('cancelar')}
          </Button>
          <Button
            onClick={handleSavePhoto}
            variant="contained"
            color="primary"
            disabled={!tempPhotoUrl || loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {translate('salvar')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewFullPhoto}
        onClose={handleCloseFullPhoto}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { backgroundColor: 'transparent', boxShadow: 'none', overflow: 'hidden' }
        }}
      >
        <DialogContent sx={{ p: 0, textAlign: 'center', position: 'relative', backgroundColor: 'transparent' }}>
          <IconButton
            onClick={handleCloseFullPhoto}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={photoUrl}
            alt={translate('fotoDePerfil')}
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: '4px',
              boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfilePhoto;
